import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  FiFileText,
  FiTrendingUp,
  FiDollarSign,
  FiClock,
  FiRefreshCw,
  FiArrowRight,
} from 'react-icons/fi';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import toast from 'react-hot-toast';
import { taxService } from '../../services/apiService';
import LoadingSpinner from '../../components/LoadingSpinner';
import StatCard from '../../components/StatCard';
import { formatCurrency, formatDate } from '../../utils/formatters';

const CYCLES = [
  { value: 'monthly', label: 'This Month' },
  { value: 'quarterly', label: 'This Quarter' },
  { value: 'yearly', label: 'This Year' },
];

const COLORS = ['#1a73e8', '#22c55e', '#f59e0b', '#ef4444', '#7c3aed', '#06b6d4'];

const TaxSummary = () => {
  const navigate = useNavigate();
  const [cycle, setCycle] = useState('monthly');

  const {
    data: summaryData,
    isLoading,
    isRefetching,
    refetch,
  } = useQuery(
    ['tax-summary', cycle],
    () => taxService.getSummary(cycle),
    { refetchOnWindowFocus: false }
  );

  const summary = summaryData?.data || {};

  const handleCreateFiling = () => {
    navigate('/tax/filing/new', {
      state: {
        cycle,
        summary,
      },
    });
  };

  if (isLoading) return <LoadingSpinner message="Loading tax summary..." />;

  const hasSales = (summary.total_transactions || 0) > 0;

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Tax Summary</h1>
          <p className="page-subtitle">
            GST collected and tax liability for the selected period
          </p>
        </div>
        <div className="page-actions">
          <button
            className="btn btn-secondary"
            onClick={() => refetch()}
            disabled={isRefetching}
          >
            <FiRefreshCw
              size={18}
              style={{
                animation: isRefetching ? 'spin 1s linear infinite' : 'none',
              }}
            />
            Refresh
          </button>
          <button
            className="btn btn-primary"
            onClick={handleCreateFiling}
            disabled={!hasSales}
          >
            <FiFileText size={18} />
            Create Filing
          </button>
        </div>
      </div>

      {/* Cycle tabs */}
      <div
        style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '20px',
          background: '#f3f4f6',
          padding: '4px',
          borderRadius: '8px',
          width: 'fit-content',
        }}
      >
        {CYCLES.map((c) => (
          <button
            key={c.value}
            className={`btn ${
              cycle === c.value ? 'btn-primary' : 'btn-secondary'
            }`}
            onClick={() => setCycle(c.value)}
            style={{ padding: '8px 20px', fontSize: '14px' }}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Period display */}
      <div
        style={{
          background: '#e8f0fe',
          border: '1px solid #1a73e8',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <FiClock size={18} color="#1a73e8" />
        <span style={{ color: '#1e40af', fontWeight: 500 }}>
          Period: {formatDate(summary.period_start)} —{' '}
          {formatDate(summary.period_end)}
        </span>
      </div>

      {/* Stat cards */}
      <div className="stats-grid">
        <StatCard
          icon={<FiDollarSign size={24} />}
          value={formatCurrency(summary.gross_sales || 0)}
          label="Gross Sales"
          color="blue"
        />
        <StatCard
          icon={<FiTrendingUp size={24} />}
          value={formatCurrency(summary.net_sales || 0)}
          label="Net Sales"
          color="green"
        />
        <StatCard
          icon={<FiFileText size={24} />}
          value={formatCurrency(summary.tax_collected || 0)}
          label="GST Collected"
          color="orange"
        />
        <StatCard
          icon={<FiClock size={24} />}
          value={summary.total_transactions || 0}
          label="Transactions"
          color="purple"
        />
      </div>

      {/* Empty state */}
      {!hasSales && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div className="empty-state">
            <FiFileText size={48} />
            <h3>No sales in this period</h3>
            <p>
              Once you make sales through the POS, they'll appear here and you
              can create a tax filing.
            </p>
          </div>
        </div>
      )}

      {hasSales && (
        <>
          {/* Breakdown grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
              gap: '24px',
              marginBottom: '24px',
            }}
          >
            {/* Tax breakdown */}
            <div className="card">
              <div className="card-header">
                <h2 className="card-title">Tax Breakdown</h2>
              </div>
              <div style={{ padding: '8px 0' }}>
                <Row
                  label="Gross Sales"
                  value={formatCurrency(summary.gross_sales || 0)}
                />
                <Row
                  label="Discounts"
                  value={`− ${formatCurrency(summary.total_discount || 0)}`}
                  valueColor="#ef4444"
                />
                <Row
                  label="Taxable Sales"
                  value={formatCurrency(summary.taxable_sales || 0)}
                  bold
                />
                <div
                  style={{
                    borderTop: '2px solid #e5e7eb',
                    margin: '12px 0',
                  }}
                />
                <Row
                  label="GST Collected (7%)"
                  value={formatCurrency(summary.tax_collected || 0)}
                  valueColor="#f59e0b"
                  bold
                />
                <Row
                  label="Tax Due"
                  value={formatCurrency(summary.tax_due || 0)}
                  valueColor="#1a73e8"
                  bold
                  large
                />
              </div>
            </div>

            {/* Payment methods */}
            <div className="card">
              <div className="card-header">
                <h2 className="card-title">Sales by Payment Method</h2>
              </div>
              {(summary.by_payment_method || []).length === 0 ? (
                <div className="empty-state">
                  <FiDollarSign size={40} />
                  <p>No payment data for this period</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={(summary.by_payment_method || []).map((m) => ({
                        name: m.method,
                        value: parseFloat(m.amount || 0),
                      }))}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name}: ${(percent * 100).toFixed(0)}%`
                      }
                      outerRadius={90}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {(summary.by_payment_method || []).map((_, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Existing filings for this period */}
          {(summary.existing_filings || []).length > 0 && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <div className="card-header">
                <h2 className="card-title">
                  Existing Filings for This Period
                </h2>
              </div>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Filing ID</th>
                      <th>Cycle</th>
                      <th>Status</th>
                      <th>Tax Due</th>
                      <th>Submitted</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.existing_filings.map((f) => (
                      <tr key={f.filing_id}>
                        <td>
                          <code style={{ fontSize: '12px' }}>
                            {f.filing_id}
                          </code>
                        </td>
                        <td>{f.cycle}</td>
                        <td>
                          <StatusBadge status={f.status} />
                        </td>
                        <td>{formatCurrency(f.tax_due || 0)}</td>
                        <td>{f.submitted_at ? formatDate(f.submitted_at) : '—'}</td>
                        <td>
                          <button
                            className="icon-btn"
                            onClick={() =>
                              navigate(`/tax/filing/${f.filing_id}`)
                            }
                          >
                            <FiArrowRight size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ---------- Helper components ----------

const Row = ({ label, value, bold, large, valueColor }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      padding: '8px 0',
      fontSize: large ? '18px' : '14px',
      fontWeight: bold ? 600 : 400,
    }}
  >
    <span style={{ color: '#6b7280' }}>{label}</span>
    <span style={{ color: valueColor || '#1f2937' }}>{value}</span>
  </div>
);

const StatusBadge = ({ status }) => {
  const colors = {
    draft: { bg: '#f3f4f6', color: '#6b7280' },
    submitted: { bg: '#dbeafe', color: '#1e40af' },
    approved: { bg: '#dcfce7', color: '#166534' },
    rejected: { bg: '#fee2e2', color: '#991b1b' },
    amended: { bg: '#fef3c7', color: '#92400e' },
  };
  const c = colors[status] || colors.draft;
  return (
    <span
      style={{
        background: c.bg,
        color: c.color,
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'capitalize',
      }}
    >
      {status}
    </span>
  );
};

export default TaxSummary;
